/**
 * Split-Flap Display
 * A production-quality virtual split-flap board with mechanical realism
 * Adapted for cloud service with WebSocket integration
 */

import {
  CHARSET,
  type BoardState,
  COLOR_MAP,
  getCellChar,
  getCellColor,
  getCellBg,
  parseColorMarkup,
  stripColorCodes,
  stripAllMarkup,
  formatClockTime,
  type ColorCode,
  type CellData,
  MIN_ROWS,
  MAX_ROWS,
  MIN_COLS,
  MAX_COLS,
} from '@flipboard/shared';

interface CellState {
  current: string;
  target: string;
  color: string;
  targetColor: string;
  bg: string | null;       // Background color hex
  targetBg: string | null; // Target background color
  animating: boolean;
  progress: number;
  charIndex: number;
  targetIndex: number;
  flipCount: number;
  xOffset: number;
  yOffset: number;
  rotationOffset: number;
  timingOffset: number;
  flipDelay: number;
  // Clock metadata
  clockFormat?: string;
  clockIndex?: number;
}

export interface SplitFlapOptions {
  rows?: number;
  cols?: number;
  speed?: number;
  soundEnabled?: boolean;
  volume?: number;
  typingMode?: boolean;
  frameEnabled?: boolean;
  showWatermark?: boolean;
  onAnimationComplete?: () => void;
}

export class SplitFlapBoard {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Configuration
  public rows: number;
  public cols: number;
  public speed: number;
  public soundEnabled: boolean;
  public volume: number;
  public typingMode: boolean;

  // Character set from shared package
  private readonly charset = CHARSET;

  // Visual constants (base values)
  private readonly baseCellWidth = 40;
  private readonly baseCellHeight = 60;
  private readonly baseGapSize = 2;
  private readonly baseCornerRadius = 3;

  // Current scaled values
  private cellWidth: number;
  private cellHeight: number;
  private gapSize: number;
  private cornerRadius: number;
  private displayScale = 1;

  // Frame settings
  private frameEnabled: boolean;
  private readonly baseFramePadding = 16;
  private readonly baseFrameRadius = 12;
  private readonly baseBottomLipHeight = 32;
  private framePadding: number;
  private frameRadius: number;
  private bottomLipHeight: number;

  // Watermark settings
  public showWatermark: boolean;

  // Callback for animation completion
  private onAnimationComplete?: () => void;

  // Colors
  private readonly bgColor = '#0f0f0f';
  private readonly dividerColor = '#1a1a1a';
  private readonly flapColor = '#f5f5f0';
  private readonly flapBgColor = '#0a0a0a';

  // State
  private cells: CellState[][] = [];
  private animating = false;
  private animationFrame: number | null = null;
  private lastTime = 0;

  // Clock management
  private clockCells: { row: number; col: number; format: string; index: number }[] = [];
  private clockTimer: number | null = null;

  // Dimensions
  private canvasWidth = 0;
  private canvasHeight = 0;
  private boardWidth = 0;
  private boardHeight = 0;

  // Sound system
  public audioContext: AudioContext | null = null;

  constructor(canvas: HTMLCanvasElement, options: SplitFlapOptions = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false })!;

    // Configuration
    this.rows = options.rows || 6;
    this.cols = options.cols || 22;
    this.speed = options.speed || 1.0;
    this.soundEnabled = options.soundEnabled === true;
    this.volume = options.volume || 0.3;
    this.typingMode = options.typingMode || false;
    this.frameEnabled = options.frameEnabled !== false;
    this.showWatermark = options.showWatermark !== false; // Default to true
    this.onAnimationComplete = options.onAnimationComplete;

    // Initialize scaled values
    this.cellWidth = this.baseCellWidth;
    this.cellHeight = this.baseCellHeight;
    this.gapSize = this.baseGapSize;
    this.cornerRadius = this.baseCornerRadius;
    this.framePadding = this.baseFramePadding;
    this.frameRadius = this.baseFrameRadius;
    this.bottomLipHeight = this.baseBottomLipHeight;

    // Initialize
    this.initAudio();
    this.initBoard();
    this.setupCanvas();
    this.render();
  }

  private initAudio(): void {
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioContext = new AudioContextClass();
    } catch {
      console.warn('Web Audio API not supported');
    }
  }

  private playClickSound(variation = 0): void {
    if (!this.soundEnabled || !this.audioContext || this.volume === 0) return;

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    const now = this.audioContext.currentTime;
    const bufferSize = this.audioContext.sampleRate * 0.04;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate
    );
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      let sample = Math.random() * 2 - 1;
      const attackSamples = this.audioContext.sampleRate * 0.005;
      if (i < attackSamples) {
        sample *= i / attackSamples;
      }
      const decayFactor = Math.exp(-i / (this.audioContext.sampleRate * 0.008));
      sample *= decayFactor;
      if (i > 0) {
        sample = sample * 0.7 + data[i - 1] * -0.3;
      }
      data[i] = sample;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1200 + variation * 300 + Math.random() * 400;
    filter.Q.value = 0.5;

    const gainNode = this.audioContext.createGain();
    const volumeVariation = 0.7 + Math.random() * 0.3;
    gainNode.gain.value = this.volume * volumeVariation;

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    source.start(now);
  }

  private initBoard(): void {
    const defaultColor = COLOR_MAP.W;
    this.cells = [];
    for (let row = 0; row < this.rows; row++) {
      this.cells[row] = [];
      for (let col = 0; col < this.cols; col++) {
        this.cells[row][col] = {
          current: ' ',
          target: ' ',
          color: defaultColor,
          targetColor: defaultColor,
          bg: null,
          targetBg: null,
          animating: false,
          progress: 0,
          charIndex: 0,
          targetIndex: 0,
          flipCount: 0,
          xOffset: (Math.random() - 0.5) * 0.3,
          yOffset: (Math.random() - 0.5) * 0.3,
          rotationOffset: (Math.random() - 0.5) * 0.5,
          timingOffset: Math.random() * 0.1,
          flipDelay: 0,
        };
      }
    }
  }

  private setupCanvas(): void {
    const boardWidth =
      this.cols * (this.cellWidth + this.gapSize) + this.gapSize;
    const boardHeight =
      this.rows * (this.cellHeight + this.gapSize) + this.gapSize;

    const frameExtra = this.frameEnabled ? this.framePadding * 2 : 0;
    const bottomExtra = this.frameEnabled ? this.bottomLipHeight : 0;

    const totalWidth = boardWidth + frameExtra;
    const totalHeight = boardHeight + frameExtra + bottomExtra;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = totalWidth * dpr;
    this.canvas.height = totalHeight * dpr;
    this.canvas.style.width = totalWidth + 'px';
    this.canvas.style.height = totalHeight + 'px';

    this.ctx.scale(dpr, dpr);

    this.canvasWidth = totalWidth;
    this.canvasHeight = totalHeight;
    this.boardWidth = boardWidth;
    this.boardHeight = boardHeight;
  }

  private charToIndex(char: string): number {
    const upper = char.toUpperCase();
    const index = this.charset.indexOf(upper);
    return index >= 0 ? index : 0;
  }

  private indexToChar(index: number): string {
    return this.charset[index % this.charset.length];
  }

  public setCell(row: number, col: number, char: string, delay = 0, color?: string, bg?: string | null): void {
    if (row < 0 || row >= this.rows || col < 0 || col >= this.cols) return;

    const cell = this.cells[row][col];
    const targetIndex = this.charToIndex(char);
    const targetColor = color || COLOR_MAP.W;
    const targetBg = bg ?? null;

    // Update target color and background (will apply when animation completes)
    cell.targetColor = targetColor;
    cell.targetBg = targetBg;

    // Check if color or background changed
    const colorChanged = cell.color !== targetColor || cell.bg !== targetBg;

    if (cell.target === char) {
      // Character hasn't changed - check if color/bg changed
      if (colorChanged && !cell.animating) {
        // Trigger a short flip cycle to animate the color change
        cell.target = char;
        cell.targetIndex = targetIndex;
        cell.charIndex = this.charToIndex(cell.current);
        cell.flipDelay = delay;
        // Do a few flips to show the color changing (5 flips is enough visual feedback)
        cell.flipCount = 5;
        cell.animating = true;
        cell.progress = 0;
      }
      return;
    }

    cell.target = char;
    cell.targetIndex = targetIndex;
    cell.charIndex = this.charToIndex(cell.current);
    cell.flipDelay = delay;

    const forward =
      (targetIndex - cell.charIndex + this.charset.length) %
      this.charset.length;
    cell.flipCount = forward || (colorChanged ? this.charset.length : 0);

    if (cell.flipCount > 0) {
      cell.animating = true;
      cell.progress = 0;
    } else {
      // No animation needed, apply color and bg immediately
      cell.color = targetColor;
      cell.bg = targetBg;
    }
  }

  public setText(text: string, typingMode = false): void {
    const lines = text.split('\n');
    let cellDelay = 0;
    const delayIncrement = typingMode ? 0.03 : 0;
    const cascadeDelay = typingMode ? 0 : 0.01;

    for (let row = 0; row < this.rows; row++) {
      const line = row < lines.length ? lines[row] : '';
      // Parse color and background markup for this line
      const parsed = parseColorMarkup(line);

      for (let col = 0; col < this.cols; col++) {
        let char = ' ';
        let color = COLOR_MAP.W;
        let bg: string | null = null;
        let clockFormat: string | undefined;
        let clockIndex: number | undefined;

        if (col < parsed.length) {
          char = parsed[col].char;
          color = COLOR_MAP[parsed[col].color as ColorCode] || COLOR_MAP.W;
          bg = parsed[col].bg ? COLOR_MAP[parsed[col].bg as ColorCode] || null : null;
          clockFormat = parsed[col].clock?.format;
          clockIndex = parsed[col].clock?.index;
        }

        this.setCell(row, col, char, cellDelay, color, bg);

        // Store clock metadata
        if (clockFormat !== undefined && clockIndex !== undefined) {
          this.cells[row][col].clockFormat = clockFormat;
          this.cells[row][col].clockIndex = clockIndex;
        } else {
          // Clear clock metadata if not present
          this.cells[row][col].clockFormat = undefined;
          this.cells[row][col].clockIndex = undefined;
        }

        cellDelay += delayIncrement + cascadeDelay;
      }
    }

    this.startAnimation();
  }

  public setMessage(text: string): void {
    // Strip all markup (colors and clocks) for formatting checks
    const stripped = stripAllMarkup(text);

    if (stripped.includes('\n')) {
      this.setText(text, this.typingMode);
      return;
    }

    // Check for any leading/trailing whitespace, multiple consecutive spaces, or no spaces
    // No spaces means it's a single word with markup - use setText to avoid truncation issues
    if (/^\s/.test(stripped) || /\s$/.test(stripped) || /\s{2,}/.test(stripped) || !stripped.includes(' ')) {
      this.setText(text, this.typingMode);
      return;
    }

    // Split stripped text for length calculations
    const words = stripped.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    // Split original text by spaces (keeping color codes)
    const originalWords = text.split(' ');
    const originalLines: string[] = [];
    let currentOriginalLine = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i]; // stripped version for length
      const originalWord = originalWords[i] || word; // with color codes
      const separator = currentLine ? ' ' : '';
      const originalSeparator = currentOriginalLine ? ' ' : '';

      if (currentLine.length + separator.length + word.length <= this.cols) {
        currentLine += separator + word;
        currentOriginalLine += originalSeparator + originalWord;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          originalLines.push(currentOriginalLine);
        }
        currentLine = word.length > this.cols ? word.substring(0, this.cols) : word;
        currentOriginalLine = stripAllMarkup(originalWord).length > this.cols
          ? originalWord.substring(0, this.cols)
          : originalWord;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
      originalLines.push(currentOriginalLine);
    }

    const finalText = originalLines.slice(0, this.rows).join('\n');
    this.setText(finalText, this.typingMode);
  }

  public clear(): void {
    this.setText('');
  }

  public setBoard(rows: number, cols: number): void {
    this.rows = Math.max(MIN_ROWS, Math.min(MAX_ROWS, rows));
    this.cols = Math.max(MIN_COLS, Math.min(MAX_COLS, cols));
    this.initBoard();
    this.setupCanvas();
    this.render();
  }

  /**
   * Apply full state from server (no animation, instant sync)
   */
  public applyState(state: BoardState): void {
    if (this.rows !== state.rows || this.cols !== state.cols) {
      this.setBoard(state.rows, state.cols);
    }

    for (let row = 0; row < state.rows; row++) {
      for (let col = 0; col < state.cols; col++) {
        const cellData = state.cells[row]?.[col] ?? ' ';
        const char = getCellChar(cellData);
        const colorCode = getCellColor(cellData);
        const bgCode = getCellBg(cellData);
        const color = colorCode ? COLOR_MAP[colorCode as ColorCode] || COLOR_MAP.W : COLOR_MAP.W;
        const bg = bgCode ? COLOR_MAP[bgCode as ColorCode] || null : null;

        // Extract clock metadata
        const clockMeta = typeof cellData === 'object' && 'clock' in cellData ? cellData.clock : undefined;

        if (this.cells[row] && this.cells[row][col]) {
          this.cells[row][col].current = char;
          this.cells[row][col].target = char;
          this.cells[row][col].color = color;
          this.cells[row][col].targetColor = color;
          this.cells[row][col].bg = bg;
          this.cells[row][col].targetBg = bg;
          this.cells[row][col].charIndex = this.charToIndex(char);
          this.cells[row][col].targetIndex = this.charToIndex(char);
          this.cells[row][col].animating = false;
          this.cells[row][col].clockFormat = clockMeta?.format;
          this.cells[row][col].clockIndex = clockMeta?.index;
        }
      }
    }

    // Detect and start clock updates
    this.detectClockCells();
    this.startClockUpdates();

    this.render();
  }

  /**
   * Apply full state from server with flip animation
   */
  public applyStateAnimated(state: BoardState): void {
    if (this.rows !== state.rows || this.cols !== state.cols) {
      this.setBoard(state.rows, state.cols);
    }

    let cellDelay = 0;
    const cascadeDelay = 0.01;

    for (let row = 0; row < state.rows; row++) {
      for (let col = 0; col < state.cols; col++) {
        const cellData = state.cells[row]?.[col] ?? ' ';
        const char = getCellChar(cellData);
        const colorCode = getCellColor(cellData);
        const bgCode = getCellBg(cellData);
        const color = colorCode ? COLOR_MAP[colorCode as ColorCode] || COLOR_MAP.W : COLOR_MAP.W;
        const bg = bgCode ? COLOR_MAP[bgCode as ColorCode] || null : null;

        // Extract clock metadata
        const clockMeta = typeof cellData === 'object' && 'clock' in cellData ? cellData.clock : undefined;

        this.setCell(row, col, char, cellDelay, color, bg);

        // Store clock metadata
        if (this.cells[row] && this.cells[row][col]) {
          this.cells[row][col].clockFormat = clockMeta?.format;
          this.cells[row][col].clockIndex = clockMeta?.index;
        }

        cellDelay += cascadeDelay;
      }
    }

    // Detect and start clock updates
    this.detectClockCells();
    this.startClockUpdates();

    this.startAnimation();
  }

  /**
   * Apply message from server with animation
   */
  public applyMessage(text: string, clear?: boolean): void {
    if (clear) {
      this.clear();
      // Wait for clear animation to complete before showing new message
      setTimeout(() => {
        this.setMessage(text);
        this.detectClockCells();
        this.startClockUpdates();
      }, 1500);
    } else {
      this.setMessage(text);
      this.detectClockCells();
      this.startClockUpdates();
    }
  }

  /**
   * Detect clock cells by scanning for clock metadata
   */
  private detectClockCells(): void {
    this.clockCells = [];

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cell = this.cells[row][col];
        if (cell.clockFormat !== undefined && cell.clockIndex !== undefined) {
          this.clockCells.push({
            row,
            col,
            format: cell.clockFormat,
            index: cell.clockIndex
          });
        }
      }
    }
  }

  /**
   * Start clock update timer
   */
  private startClockUpdates(): void {
    this.stopClockUpdates();

    if (this.clockCells.length === 0) return;

    // Determine update interval based on formats
    let needsSeconds = false;
    for (const clockCell of this.clockCells) {
      if (clockCell.format.includes('ss')) {
        needsSeconds = true;
        break;
      }
    }

    const updateInterval = needsSeconds ? 1000 : 60000;

    // Update immediately
    this.updateClockCells();

    // Schedule periodic updates
    this.clockTimer = window.setInterval(() => {
      this.updateClockCells();
    }, updateInterval);
  }

  /**
   * Stop clock update timer
   */
  private stopClockUpdates(): void {
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  /**
   * Update all clock cells with current time
   */
  private updateClockCells(): void {
    if (this.clockCells.length === 0) return;

    const now = new Date();

    // Group by format to avoid redundant formatting
    const formatGroups = new Map<string, typeof this.clockCells>();
    for (const clockCell of this.clockCells) {
      if (!formatGroups.has(clockCell.format)) {
        formatGroups.set(clockCell.format, []);
      }
      formatGroups.get(clockCell.format)!.push(clockCell);
    }

    // Update each cell
    for (const [format, cells] of formatGroups) {
      const formatted = formatClockTime(format, now);

      for (const { row, col, index } of cells) {
        const newChar = formatted[index] || ' ';
        const cell = this.cells[row][col];

        // Skip update if cell is still animating (prevents continuous scrolling)
        if (cell.animating) continue;

        // Only update if character changed
        // Use targetColor/targetBg to preserve colors even if previous animation hasn't completed
        if (cell.current !== newChar || cell.target !== newChar) {
          this.setCell(row, col, newChar, 0, cell.targetColor, cell.targetBg);
        }
      }
    }

    this.startAnimation();
  }

  public fitToSize(maxWidth: number, maxHeight: number): void {
    const baseWidth =
      this.cols * (this.baseCellWidth + this.baseGapSize) + this.baseGapSize;
    const baseHeight =
      this.rows * (this.baseCellHeight + this.baseGapSize) + this.baseGapSize;

    const frameExtra = this.frameEnabled ? this.baseFramePadding * 2 : 0;
    const bottomExtra = this.frameEnabled ? this.baseBottomLipHeight : 0;

    const totalBaseWidth = baseWidth + frameExtra;
    const totalBaseHeight = baseHeight + frameExtra + bottomExtra;

    const scaleX = maxWidth / totalBaseWidth;
    const scaleY = maxHeight / totalBaseHeight;
    const scale = Math.min(scaleX, scaleY);

    this.setDisplayScale(scale);
  }

  private setDisplayScale(scale: number): void {
    this.displayScale = scale;
    this.cellWidth = this.baseCellWidth * scale;
    this.cellHeight = this.baseCellHeight * scale;
    this.gapSize = this.baseGapSize * scale;
    this.cornerRadius = this.baseCornerRadius * scale;
    this.framePadding = this.baseFramePadding * scale;
    this.frameRadius = this.baseFrameRadius * scale;
    this.bottomLipHeight = this.baseBottomLipHeight * scale;

    this.setupCanvas();
    this.render();
  }

  private startAnimation(): void {
    if (this.animating) return;
    this.animating = true;
    this.lastTime = performance.now();
    this.animate();
  }

  private animate(currentTime = performance.now()): void {
    if (!this.animating) return;

    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    let anyAnimating = false;

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cell = this.cells[row][col];

        if (cell.animating) {
          if (cell.flipDelay > 0) {
            cell.flipDelay -= deltaTime;
            anyAnimating = true;
            continue;
          }

          const baseFlipSpeed = 12 * this.speed;
          const progressRatio = cell.progress;
          let speedMultiplier = 1.0;

          if (progressRatio < 0.15) {
            speedMultiplier = 0.6 + (progressRatio / 0.15) * 0.4;
          } else if (progressRatio > 0.85) {
            speedMultiplier = 0.6 + ((1 - progressRatio) / 0.15) * 0.4;
          }

          const flipSpeed = baseFlipSpeed * speedMultiplier;
          const progressDelta =
            (flipSpeed * deltaTime) / Math.max(cell.flipCount, 1);

          const oldCharIndex = Math.floor(cell.progress * cell.flipCount);
          cell.progress += progressDelta + cell.timingOffset * deltaTime * 0.1;
          const newCharIndex = Math.floor(cell.progress * cell.flipCount);

          if (newCharIndex > oldCharIndex) {
            const variation = (row * this.cols + col) % 5;
            this.playClickSound(variation);
          }

          if (cell.progress >= 1.0) {
            cell.progress = 1.0;
            cell.current = cell.target;
            cell.color = cell.targetColor;
            cell.bg = cell.targetBg;
            cell.animating = false;
          } else {
            anyAnimating = true;
          }
        }
      }
    }

    this.render();

    if (anyAnimating) {
      this.animationFrame = requestAnimationFrame((t) => this.animate(t));
    } else {
      this.animating = false;
      // Notify completion callback if set
      if (this.onAnimationComplete) {
        this.onAnimationComplete();
      }
    }
  }

  private render(): void {
    const ctx = this.ctx;

    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    if (this.frameEnabled) {
      this.renderFrame(ctx);
    }

    const offsetX = this.frameEnabled ? this.framePadding : 0;
    const offsetY = this.frameEnabled ? this.framePadding : 0;

    ctx.fillStyle = this.bgColor;
    if (this.frameEnabled) {
      this.roundRect(
        ctx,
        offsetX,
        offsetY,
        this.boardWidth,
        this.boardHeight,
        this.frameRadius * 0.5
      );
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    }

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cell = this.cells[row][col];
        const x =
          offsetX +
          this.gapSize +
          col * (this.cellWidth + this.gapSize) +
          cell.xOffset * this.displayScale;
        const y =
          offsetY +
          this.gapSize +
          row * (this.cellHeight + this.gapSize) +
          cell.yOffset * this.displayScale;

        this.renderCell(ctx, cell, x, y);
      }
    }

    // Render watermark last (on top)
    if (this.showWatermark) {
      this.renderWatermark(ctx);
    }
  }

  private renderFrame(ctx: CanvasRenderingContext2D): void {
    const padding = this.framePadding;
    const radius = this.frameRadius;
    const lipHeight = this.bottomLipHeight;

    const frameGradient = ctx.createLinearGradient(0, 0, 0, this.canvasHeight);
    frameGradient.addColorStop(0, '#2a2a2a');
    frameGradient.addColorStop(0.3, '#1f1f1f');
    frameGradient.addColorStop(0.7, '#1a1a1a');
    frameGradient.addColorStop(1, '#151515');

    ctx.fillStyle = frameGradient;
    this.roundRect(ctx, 0, 0, this.canvasWidth, this.canvasHeight, radius);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = Math.max(1, this.displayScale);
    this.roundRect(
      ctx,
      0.5,
      0.5,
      this.canvasWidth - 1,
      this.canvasHeight - 1,
      radius
    );
    ctx.stroke();

    const innerX = padding - 2 * this.displayScale;
    const innerY = padding - 2 * this.displayScale;
    const innerW = this.boardWidth + 4 * this.displayScale;
    const innerH = this.boardHeight + 4 * this.displayScale;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this.roundRect(ctx, innerX, innerY, innerW, innerH, radius * 0.6);
    ctx.fill();

    const lipY = this.canvasHeight - lipHeight;

    this.renderEmbossedText(
      ctx,
      'PANELICIOUS',
      this.canvasWidth / 2,
      lipY + lipHeight / 2
    );
  }

  private renderEmbossedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number
  ): void {
    const fontSize = Math.round(14 * this.displayScale);
    ctx.font = `600 ${fontSize}px "Manrope", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const letterSpacing = 4 * this.displayScale;
    const chars = text.split('');
    const totalWidth =
      ctx.measureText(text).width + letterSpacing * (chars.length - 1);
    let currentX = x - totalWidth / 2;

    for (const char of chars) {
      const charWidth = ctx.measureText(char).width;

      // Shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillText(char, currentX + charWidth / 2, y + 1 * this.displayScale);

      // Highlight
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fillText(char, currentX + charWidth / 2, y - 0.5 * this.displayScale);

      // Main text
      ctx.fillStyle = '#252525';
      ctx.fillText(char, currentX + charWidth / 2, y);

      currentX += charWidth + letterSpacing;
    }
  }

  private renderWatermark(ctx: CanvasRenderingContext2D): void {
    const text = 'split.panelicious.com';
    const fontSize = Math.max(10, Math.round(10 * this.displayScale));
    const padding = 8 * this.displayScale;

    ctx.save();
    ctx.font = `500 ${fontSize}px "Manrope", sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';

    const x = this.canvasWidth - padding;
    const y = this.canvasHeight - padding;

    // Measure text for background pill
    const textWidth = ctx.measureText(text).width;
    const pillPadding = 4 * this.displayScale;
    const pillHeight = fontSize + pillPadding * 2;
    const pillWidth = textWidth + pillPadding * 2;

    // Semi-transparent background pill
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    this.roundRect(
      ctx,
      x - pillWidth - pillPadding,
      y - pillHeight,
      pillWidth + pillPadding,
      pillHeight,
      4 * this.displayScale
    );
    ctx.fill();

    // Watermark text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText(text, x - pillPadding, y - pillPadding);

    ctx.restore();
  }

  private renderCell(
    ctx: CanvasRenderingContext2D,
    cell: CellState,
    x: number,
    y: number
  ): void {
    ctx.save();

    let displayChar = cell.current;
    let rotation = 0;

    if (cell.animating && cell.flipDelay <= 0) {
      const flipProgress = cell.progress * cell.flipCount;
      const currentFlipIndex = Math.floor(flipProgress);
      const flipPhase = flipProgress - currentFlipIndex;

      const charIndexInSequence =
        (cell.charIndex + currentFlipIndex) % this.charset.length;
      displayChar = this.indexToChar(charIndexInSequence);

      rotation = flipPhase * Math.PI;
    }

    ctx.fillStyle = this.dividerColor;
    this.roundRect(ctx, x, y, this.cellWidth, this.cellHeight, this.cornerRadius);
    ctx.fill();

    const centerX = x + this.cellWidth / 2;
    const centerY = y + this.cellHeight / 2;

    ctx.translate(centerX, centerY);

    const flapPadding = 4 * this.displayScale;
    const flapWidth = this.cellWidth - flapPadding;
    const flapHeight = this.cellHeight - flapPadding;

    // Determine background color
    const hasBgColor = cell.bg !== null;
    let baseBgColor = this.flapBgColor;

    if (hasBgColor && cell.bg) {
      baseBgColor = cell.bg;
    }

    if (rotation > 0) {
      const scale = Math.abs(Math.cos(rotation));
      ctx.scale(1, scale);

      const showingBack = Math.sin(rotation) < 0;

      if (showingBack) {
        ctx.fillStyle = hasBgColor ? this.darkenColor(baseBgColor, 0.3) : '#050505';
      } else {
        ctx.fillStyle = baseBgColor;
      }
    } else {
      ctx.fillStyle = baseBgColor;
    }

    this.roundRect(
      ctx,
      -flapWidth / 2,
      -flapHeight / 2,
      flapWidth,
      flapHeight,
      this.cornerRadius
    );
    ctx.fill();

    // Apply 3D shading gradient
    if (rotation === 0 || Math.sin(rotation) >= 0) {
      const gradient = ctx.createLinearGradient(
        0,
        -flapHeight / 2,
        0,
        flapHeight / 2
      );

      if (hasBgColor) {
        // For colored backgrounds: brighter top, darker bottom for 3D effect
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
        gradient.addColorStop(0.45, 'rgba(255, 255, 255, 0.08)');
        gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.15)');
        gradient.addColorStop(0.55, 'rgba(0, 0, 0, 0.20)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
      } else {
        // Default dark background shading
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');
      }

      ctx.fillStyle = gradient;
      this.roundRect(
        ctx,
        -flapWidth / 2,
        -flapHeight / 2,
        flapWidth,
        flapHeight,
        this.cornerRadius
      );
      ctx.fill();
    }

    // Draw text
    if (rotation === 0 || Math.sin(rotation) >= 0) {
      ctx.fillStyle = cell.color || this.flapColor;

      const fontSize = Math.round(32 * this.displayScale);
      ctx.font = `500 ${fontSize}px "Manrope", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (Math.random() < 0.3) {
        ctx.globalAlpha = 0.95 + Math.random() * 0.05;
      }

      ctx.fillText((displayChar || ' ').toUpperCase(), 0, 0);
      ctx.globalAlpha = 1;
    }

    if (rotation === 0) {
      // Cell border
      ctx.strokeStyle = hasBgColor ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.3)';
      ctx.lineWidth = Math.max(1, this.displayScale);
      this.roundRect(
        ctx,
        -flapWidth / 2,
        -flapHeight / 2,
        flapWidth,
        flapHeight,
        this.cornerRadius
      );
      ctx.stroke();

      // Draw the split-flap divider line through the middle
      const lineWidth = Math.max(1.5, 1.5 * this.displayScale);

      // Dark line (the gap between flaps)
      ctx.strokeStyle = hasBgColor ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)';
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(-flapWidth / 2 + this.cornerRadius * 0.5, 0);
      ctx.lineTo(flapWidth / 2 - this.cornerRadius * 0.5, 0);
      ctx.stroke();

      // Subtle highlight below the line for depth
      ctx.strokeStyle = hasBgColor ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = Math.max(1, this.displayScale * 0.5);
      ctx.beginPath();
      ctx.moveTo(-flapWidth / 2 + this.cornerRadius * 0.5, lineWidth * 0.7);
      ctx.lineTo(flapWidth / 2 - this.cornerRadius * 0.5, lineWidth * 0.7);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Darken a hex color by a factor (0-1)
   */
  private darkenColor(hex: string, factor: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const dr = Math.round(r * (1 - factor));
    const dg = Math.round(g * (1 - factor));
    const db = Math.round(b * (1 - factor));

    return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  public destroy(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.stopClockUpdates();
  }
}
